import {
  Directive,
  ElementRef,
  inject,
  InputSignal,
  NgZone,
  OnDestroy,
  input,
  effect,
  untracked,
  output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  createTooltip,
  setTooltipPosition,
  watchTooltipAnchor,
  TTooltipAnchorWatcher,
  TTooltipConfig,
} from './tooltip';
import { documentOf } from '../anchor-document.util';

type TYPosition = 'start' | 'center-start' | 'center' | 'center-end' | 'end';
type TXPosition = 'start' | 'center' | 'end';

@Directive({
  selector: '[lib-tooltip]',
  standalone: true,
  exportAs: 'libTooltip',
})
export class TooltipDirective implements OnDestroy {
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly translateService = inject(TranslateService);
  private readonly ngZone: NgZone = inject(NgZone);
  private tooltip: HTMLDivElement | null = null;
  private isTooltipVisible: boolean = false;
  private anchorWatcher: TTooltipAnchorWatcher | null = null;
  /** `body` onde os guardas de scroll estão presos — o da âncora na altura. */
  private guardedBody: HTMLElement | null = null;

  public tooltipTitle: InputSignal<string> = input<string>();
  public tooltipContent: InputSignal<string> = input<string>();
  public tooltipMultiples: InputSignal<{ title?: string; content?: string; }[]> = input<{ title?: string; content?: string }[]>();
  public hasDistance: InputSignal<boolean> = input<boolean>(true);
  public disableTooltip: InputSignal<boolean> = input<boolean>(false);
  public readonly openOnClick: InputSignal<boolean> = input<boolean>(false);
  public removedTooltip = output<void>();

  public xAxis: InputSignal<TXPosition> = input<TXPosition>('start');
  public yAxis: InputSignal<TYPosition> = input<TYPosition>('end');
  public distance: InputSignal<number> = input<number>(6);

  public ngOnDestroy(): void {
    this.removeTooltip();
    this.removeListeners();
  }

  private removeListeners(): void {
    const element = this.elementRef?.nativeElement;
    if (element) {
      element.removeEventListener('mouseenter', this.createTooltip);
      element.removeEventListener('click', this.handleClick);
      element.removeEventListener('mouseleave', this.removeTooltip);
    }
    this.removeScrollGuards();
  }

  /**
   * Scrolling closes an open tooltip: the anchor moved out from under it. The
   * guards go on the anchor's own document, which is not the global one when
   * the anchor lives in a detached (Picture-in-Picture) window; the body is
   * remembered so the removal hits the same one even if the anchor moves.
   */
  private addScrollGuards(): void {
    this.removeScrollGuards();
    const body = documentOf(this.elementRef?.nativeElement)?.body ?? null;
    this.ngZone.runOutsideAngular(() => {
      body?.addEventListener('wheel', this.removeTooltip, { passive: true });
      body?.addEventListener('touchmove', this.removeTooltip, { passive: true });
    });
    this.guardedBody = body;
  }

  /** `document.body` is null while the document is being torn down (leaving the
   * page, iframe removed) and a change-detection tick can still land here. */
  private removeScrollGuards(): void {
    this.guardedBody?.removeEventListener('wheel', this.removeTooltip);
    this.guardedBody?.removeEventListener('touchmove', this.removeTooltip);
    this.guardedBody = null;
  }

  private readonly observeToCreateTooltip = effect(() => {
    const title = this.tooltipTitle();
    const content = this.tooltipContent();
    const multiples = this.tooltipMultiples();
    const disableTooltip = this.disableTooltip();
    const openOnClick = this.openOnClick();
    
    untracked(() => {
      this.removeListeners();
      const element = this.elementRef?.nativeElement;
      if (!element) return;
      if ((title || content || multiples?.length) && !disableTooltip) {
        if (openOnClick) {
          element.addEventListener('click', this.handleClick, { passive: true });
        } else {
          element.addEventListener('mouseenter', this.createTooltip, { passive: true });
        }
        element.addEventListener('mouseleave', this.removeTooltip, { passive: true });
        this.addScrollGuards();
        if (this.isTooltipVisible) {
          this.removeTooltip();
          this.createTooltip();
        }
      } else {
        // `removeListeners()` já correu: sem `mouseleave` nada voltaria a fechar
        // um tooltip visível, mesmo quando o motivo não é `disableTooltip`.
        this.removeTooltip();
      }
    });
  });

  private readonly handleClick = (): void => {
    if (this.disableTooltip()) return;
    if (this.isTooltipVisible) return;
    this.createTooltip();
  };

  public readonly removeTooltip = (): void => {
    this.removeScrollGuards();
    this.anchorWatcher?.stop();
    this.anchorWatcher = null;
    if (this.tooltip) {
      this.tooltip.remove();
      this.tooltip = null;
      this.isTooltipVisible = false;
      this.removedTooltip.emit();
    }
  };

  private readonly createTooltip = (): void => {
    if (this.disableTooltip() || this.isTooltipVisible) return;
    
    const config: TTooltipConfig = {
      content: this.tooltipContent() ? this.translateService.instant(this.tooltipContent()) : null,
      title: this.tooltipTitle() ? this.translateService.instant(this.tooltipTitle()) : null,
      multiples: this.tooltipMultiples()?.map(item => ({
            title: item.title ? this.translateService.instant(item.title) : null,
            content: item.content ? this.translateService.instant(item.content) : null,
          })) || null,
      element: this.elementRef.nativeElement,
      hasDistance: this.hasDistance(),
      xAxis: this.xAxis(),
      yAxis: this.yAxis(),
      distance: this.distance(),
    };
    this.tooltip = createTooltip(config);
    // Sem `document.body` não há onde montar o tooltip: nada ficou visível, por
    // isso também não se arma o watcher nem se marca o estado como aberto.
    if (!this.tooltip) return;

    this.isTooltipVisible = true;
    this.addScrollGuards();
    this.anchorWatcher = watchTooltipAnchor(this.elementRef.nativeElement, {
      onHidden: this.removeTooltip,
      onMove: () => {
        if (this.tooltip) setTooltipPosition(this.tooltip, config);
      },
    });
    if (this.openOnClick()) {
      this.tooltip.classList.add('animate-shaking');
    }
  };
}